import { useState, useEffect, useCallback } from 'preact/hooks';
import { api } from './api';
import { checkAchievements, ACHIEVEMENTS } from '../game/achievements';

export const WORLD_UNLOCK_CONFIG: Record<number, { starsPrice: number }> = {
  2: { starsPrice: 70 },
  3: { starsPrice: 210 },
};

export interface GameStateHook {
  user: any | null;
  progress: Map<number, { stars: number; bestMoves: number; completed: boolean }>;
  hasUnlimitedUndo: boolean;
  streak: number;
  totalStars: number;
  levelsCompleted: number;
  achievements: Set<string>;
  worldUnlocks: Set<number>;
  loading: boolean;

  getNextUncompletedLevel: () => number;
  saveProgress: (levelId: number, moves: number, stars: number) => Promise<string[]>;
  isWorldUnlocked: (worldId: number) => boolean;
  checkAndUnlock: () => string[];
  setUnlimitedUndo: (val: boolean) => void;
  refreshUser: () => Promise<void>;
}

// Local storage keys
const LS_PROGRESS = 'sokoban_progress';
const LS_UNDO = 'sokoban_unlimited_undo';
const LS_STREAK = 'sokoban_streak';
const LS_ACHIEVEMENTS = 'sokoban_achievements';
const LS_WORLD_UNLOCKS = 'sokoban_world_unlocks';
// Levels whose server save failed (offline or 5xx) and need retry on next online mount.
const LS_PENDING_SYNC = 'sokoban_pending_sync';

type ProgressEntry = { stars: number; bestMoves: number; completed: boolean };

function loadLocalProgress(): Map<number, ProgressEntry> {
  try {
    const raw = localStorage.getItem(LS_PROGRESS);
    if (raw) {
      const arr = JSON.parse(raw);
      return new Map(arr);
    }
  } catch {}
  return new Map();
}

function saveLocalProgress(progress: Map<number, ProgressEntry>) {
  localStorage.setItem(LS_PROGRESS, JSON.stringify([...progress]));
}

function loadPendingSyncIds(): Set<number> {
  try {
    const raw = localStorage.getItem(LS_PENDING_SYNC);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) return new Set(arr);
    }
  } catch {}
  return new Set();
}

function savePendingSyncIds(ids: Set<number>) {
  if (ids.size === 0) {
    localStorage.removeItem(LS_PENDING_SYNC);
  } else {
    localStorage.setItem(LS_PENDING_SYNC, JSON.stringify([...ids]));
  }
}

// Merge local and server progress maps, preferring the best of each per level.
// Never lose data: if server has a level that local doesn't, keep it; if local
// has improvements over server (e.g. completed offline), keep those too. The
// server's UPSERT is idempotent (GREATEST stars, LEAST moves), so the retry
// queue will converge on the merged value.
function mergeProgress(
  server: Map<number, ProgressEntry>,
  local: Map<number, ProgressEntry>,
): Map<number, ProgressEntry> {
  const merged = new Map(server);
  for (const [levelId, localEntry] of local) {
    const serverEntry = merged.get(levelId);
    if (!serverEntry) {
      merged.set(levelId, localEntry);
    } else {
      merged.set(levelId, {
        stars: Math.max(serverEntry.stars, localEntry.stars),
        bestMoves: Math.min(serverEntry.bestMoves, localEntry.bestMoves),
        completed: serverEntry.completed || localEntry.completed,
      });
    }
  }
  return merged;
}

export function useGameState(): GameStateHook {
  const [user, setUser] = useState<any>(null);
  const [progress, setProgress] = useState(() => loadLocalProgress());
  const [hasUnlimitedUndo, setUnlimitedUndoState] = useState(() => localStorage.getItem(LS_UNDO) === 'true');
  const [streak, setStreak] = useState(() => {
    const stored = localStorage.getItem(LS_STREAK);
    return stored ? parseInt(stored) : 0;
  });
  const [achievements, setAchievements] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(LS_ACHIEVEMENTS);
      if (raw) return new Set(JSON.parse(raw));
    } catch {}
    return new Set();
  });
  const [worldUnlocks, setWorldUnlocks] = useState<Set<number>>(() => {
    try {
      const raw = localStorage.getItem(LS_WORLD_UNLOCKS);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length > 0) return new Set(arr);
      }
    } catch {}
    return new Set([1]);
  });
  const [loading, setLoading] = useState(true);

  // Load user data from server on mount
  useEffect(() => {
    (async () => {
      try {
        const userData = await api.getUser();
        setUser(userData);
        if (userData.hasUnlimitedUndo) setUnlimitedUndoState(true);
        if (userData.streak) setStreak(userData.streak);
        if (userData.worldUnlocks) {
          setWorldUnlocks(new Set(userData.worldUnlocks));
          localStorage.setItem(LS_WORLD_UNLOCKS, JSON.stringify(userData.worldUnlocks));
        }

        // Sync server progress — merge with local so offline completions are preserved
        const serverProgress = await api.getProgress();
        if (Array.isArray(serverProgress)) {
          const serverMap = new Map<number, ProgressEntry>();
          for (const p of serverProgress) {
            serverMap.set(p.levelId, {
              stars: p.starsEarned,
              bestMoves: p.bestMoves,
              completed: p.completed,
            });
          }
          const merged = mergeProgress(serverMap, loadLocalProgress());
          setProgress(merged);
          saveLocalProgress(merged);

          // Retry any saves that failed during previous offline sessions. The
          // server UPSERT is idempotent, so retrying with the current local
          // best value is safe even if the server already has an equal or
          // better record.
          const pending = loadPendingSyncIds();
          if (pending.size > 0) {
            const stillPending = new Set<number>();
            for (const levelId of pending) {
              const entry = merged.get(levelId);
              if (!entry) continue; // orphaned entry; drop it
              try {
                await api.saveProgress(levelId, entry.bestMoves, entry.stars);
              } catch {
                stillPending.add(levelId);
              }
            }
            savePendingSyncIds(stillPending);
          }
        }
      } catch {
        // Offline mode - use local data
      }
      setLoading(false);
    })();
  }, []);

  const saveProgressFn = useCallback(async (levelId: number, moves: number, stars: number): Promise<string[]> => {
    // Compute the next progress map inside the setProgress callback so we always
    // see the latest state (safe against rapid concurrent saves). The callback
    // runs synchronously in Preact, so `updatedProgress` is guaranteed assigned
    // before the setProgress call returns — hence the definite-assignment
    // assertion (`!:`) rather than a nullable type + null check.
    let updatedProgress!: Map<number, ProgressEntry>;
    setProgress(prev => {
      const next = new Map(prev);
      const existing = next.get(levelId);
      if (!existing || moves < existing.bestMoves) {
        next.set(levelId, { stars: Math.max(stars, existing?.stars ?? 0), bestMoves: moves, completed: true });
      } else if (stars > existing.stars) {
        next.set(levelId, { ...existing, stars });
      }
      saveLocalProgress(next);
      updatedProgress = next;
      return next;
    });

    // Check achievements using the updated progress
    const totalStarsNow = [...updatedProgress.values()].reduce((sum, p) => sum + p.stars, 0);
    const levelsCompletedNow = [...updatedProgress.values()].filter(p => p.completed).length;
    const threeStarCountNow = [...updatedProgress.values()].filter(p => p.stars >= 3).length;

    let newAchievements: string[] = [];
    setAchievements(prevAch => {
      const earned = checkAchievements({
        levelsCompleted: levelsCompletedNow,
        totalStars: totalStarsNow,
        threeStarCount: threeStarCountNow,
        streak,
        worldsUnlocked: worldUnlocks.size,
      });
      const newlyUnlocked = [...earned].filter(id => !prevAch.has(id));
      if (newlyUnlocked.length > 0) {
        const merged = new Set([...prevAch, ...newlyUnlocked]);
        localStorage.setItem(LS_ACHIEVEMENTS, JSON.stringify([...merged]));
        newAchievements = newlyUnlocked;
        return merged;
      }
      return prevAch;
    });

    try {
      await api.saveProgress(levelId, moves, stars);
    } catch {
      // Queue for retry on next online mount. Storing the level ID (not the
      // moves/stars at time of failure) means we'll always retry with the
      // LATEST local best value, which the server's idempotent UPSERT
      // handles correctly.
      const pending = loadPendingSyncIds();
      pending.add(levelId);
      savePendingSyncIds(pending);
    }

    return newAchievements;
  }, [streak, worldUnlocks]);

  const getNextUncompletedLevel = useCallback(() => {
    for (let i = 1; i <= 100; i++) {
      if (!progress.get(i)?.completed) return i;
    }
    return 1; // All complete, start over
  }, [progress]);

  const isWorldUnlocked = useCallback((worldId: number) => {
    return worldUnlocks.has(worldId);
  }, [worldUnlocks]);

  const setUnlimitedUndo = useCallback((val: boolean) => {
    setUnlimitedUndoState(val);
    localStorage.setItem(LS_UNDO, String(val));
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const userData = await api.getUser();
      setUser(userData);
      if (userData.hasUnlimitedUndo !== undefined) {
        setUnlimitedUndoState(userData.hasUnlimitedUndo);
        localStorage.setItem(LS_UNDO, String(userData.hasUnlimitedUndo));
      }
      if (userData.streak !== undefined) {
        setStreak(userData.streak);
        localStorage.setItem(LS_STREAK, String(userData.streak));
      }
      if (userData.worldUnlocks) {
        setWorldUnlocks(new Set(userData.worldUnlocks));
        localStorage.setItem(LS_WORLD_UNLOCKS, JSON.stringify(userData.worldUnlocks));
      }
    } catch {}
  }, []);

  const checkAndUnlock = useCallback((): string[] => {
    const totalStarsNow = [...progress.values()].reduce((sum, p) => sum + p.stars, 0);
    const levelsCompletedNow = [...progress.values()].filter(p => p.completed).length;
    const threeStarCountNow = [...progress.values()].filter(p => p.stars >= 3).length;

    const earned = checkAchievements({
      levelsCompleted: levelsCompletedNow,
      totalStars: totalStarsNow,
      threeStarCount: threeStarCountNow,
      streak,
      worldsUnlocked: worldUnlocks.size,
    });

    const newlyUnlocked = [...earned].filter(id => !achievements.has(id));
    if (newlyUnlocked.length > 0) {
      const merged = new Set([...achievements, ...newlyUnlocked]);
      setAchievements(merged);
      localStorage.setItem(LS_ACHIEVEMENTS, JSON.stringify([...merged]));
    }
    return newlyUnlocked;
  }, [progress, streak, worldUnlocks, achievements]);

  const totalStars = [...progress.values()].reduce((sum, p) => sum + p.stars, 0);
  const levelsCompleted = [...progress.values()].filter(p => p.completed).length;

  return {
    user,
    progress,
    hasUnlimitedUndo,
    streak,
    totalStars,
    levelsCompleted,
    achievements,
    worldUnlocks,
    loading,
    getNextUncompletedLevel,
    saveProgress: saveProgressFn,
    isWorldUnlocked,
    checkAndUnlock,
    setUnlimitedUndo,
    refreshUser,
  };
}
